import React, { useCallback } from "react";
import { useInteractionPrefill } from "../../../hooks/useInteractionPrefill";
import { AIPrefillInteractionResponse } from "../../../types/compassAITypes";
import { MESSAGES } from "../../../messages";
import { InlinePrefillBase } from "./InlinePrefillBase";

// =============================================================================
// TYPES
// =============================================================================

export interface InlineInteractionPrefillProps {
  /** Engagement record ID (required to enable prefill) */
  engagementRecordId: string | null;
  /** Callback when prefill completes successfully */
  onSuccess?: (result: AIPrefillInteractionResponse) => void;
  /** Custom class name */
  className?: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Inline AI prefill component for the interaction (engagement update) form.
 * Thin wrapper around InlinePrefillBase that wires up useInteractionPrefill.
 */
export const InlineInteractionPrefill: React.FC<
  InlineInteractionPrefillProps
> = ({ engagementRecordId, onSuccess, className }) => {
  const {
    uploadedFiles,
    addFile,
    removeFile,
    startPrefill,
    reset,
    isUploading,
    isExtracting,
    canAddMoreFiles,
  } = useInteractionPrefill();

  const handleExtract = useCallback(
    async (prompt: string): Promise<AIPrefillInteractionResponse | null> => {
      if (!engagementRecordId) return null;
      const result = await startPrefill(engagementRecordId, prompt);
      if (result && onSuccess) {
        onSuccess(result);
      }
      return result;
    },
    [engagementRecordId, startPrefill, onSuccess],
  );

  return (
    <InlinePrefillBase
      isVisible={!!engagementRecordId}
      extractingLabel={
        MESSAGES.COMPASS_AI.INTERACTION_PREFILL.DESCRIPTION.FILLING
      }
      textPlaceholder="Paste meeting notes, interaction details, or any relevant text..."
      onExtract={handleExtract}
      uploadedFiles={uploadedFiles}
      addFile={addFile}
      removeFile={removeFile}
      reset={reset}
      isUploading={isUploading}
      isExtracting={isExtracting}
      canAddMoreFiles={canAddMoreFiles}
      className={className}
    />
  );
};
